// client/src/dnd/emitter_tool/components/EnemyToken.js
import React, { useEffect, useRef, useState } from "react";

const TOKEN_ID = "enemy_token";

function pickMainCanvas() {
  if (typeof document === "undefined") return null;
  const canvases = Array.from(document.querySelectorAll("canvas"));
  if (!canvases.length) return null;
  let best = canvases[0];
  let bestArea = (best.width || 0) * (best.height || 0);
  for (let i = 1; i < canvases.length; i++) {
    const c = canvases[i];
    const area = (c.width || 0) * (c.height || 0);
    if (area > bestArea) {
      best = c;
      bestArea = area;
    }
  }
  return best;
}

export default function EnemyToken() {
  const [screenPos, setScreenPos] = useState({
    left: -99999,
    top: -99999,
    size: 0,
  });

  const tokenImagePosRef = useRef(null); // { x, y, radius }
  const lastPayloadRef = useRef(null);
  const pxPerMeterRef = useRef(50); // Default to 50px

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateVisualAndTokens = () => {
      const tokImg = tokenImagePosRef.current;
      if (!tokImg) return;

      const canvas = pickMainCanvas();
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const iw = canvas.width || rect.width || 1;
      const ih = canvas.height || rect.height || 1;

      const sx = (rect.width || iw) / iw;
      const sy = (rect.height || ih) / ih;

      const cxScreen = rect.left + tokImg.x * sx;
      const cyScreen = rect.top + tokImg.y * sy;

      const sizeScreen = tokImg.radius * 2 * Math.max(sx, sy);

      setScreenPos({
        left: cxScreen - sizeScreen / 2,
        top: cyScreen - sizeScreen / 2,
        size: sizeScreen,
      });

      try {
        const tokenForCollision = {
          id: TOKEN_ID,
          x: tokImg.x,
          y: tokImg.y,
          radius: tokImg.radius,
        };

        const list = Array.isArray(window.__PA_TOKENS)
          ? window.__PA_TOKENS.filter((t) => t && t.id !== TOKEN_ID)
          : [];

        list.push(tokenForCollision);
        window.__PA_TOKENS = list;
      } catch {
        
      }
    };

    const computeTokenFromWalls = (payload) => {
      if (!payload) return;
      const polys =
        payload.polys ||
        payload.polygons ||
        (Array.isArray(payload) ? payload : null);
      if (!Array.isArray(polys) || polys.length === 0) return;

      const roomPolys = polys.length > 1 ? polys.slice(1) : polys;

      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity;

      for (const poly of roomPolys) {
        if (!Array.isArray(poly)) continue;
        for (const pt of poly) {
          if (!pt) continue;
          const x = Number(pt.x);
          const y = Number(pt.y);
          if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }

      if (
        !Number.isFinite(minX) ||
        !Number.isFinite(maxX) ||
        !Number.isFinite(minY) ||
        !Number.isFinite(maxY)
      ) {
        return;
      }

      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      
      // Fix: Token radius is always 1 metre, which is pxPerMeter / 2
      const radius = Math.max(8, pxPerMeterRef.current / 2);

      tokenImagePosRef.current = { x: cx, y: cy, radius };
      updateVisualAndTokens();
    };

    const onWallsUpdated = (e) => {
      const detail = e && e.detail ? e.detail : null;
      lastPayloadRef.current = detail;
      computeTokenFromWalls(detail);
    };
    
    const onGridConfigChange = (e) => {
        const squareLength = e?.detail?.config?.square_length;
        if (Number.isFinite(squareLength) && squareLength > 0) {
            pxPerMeterRef.current = squareLength;
            // Recalculate token position and size based on new scale
            computeTokenFromWalls(lastPayloadRef.current);
        }
    };

    window.addEventListener("EmitterTool:WallsUpdated", onWallsUpdated);
    window.addEventListener("EmitterTool:UI:GridConfigChange", onGridConfigChange);

    if (window.__EmitterWallsLast) {
      lastPayloadRef.current = window.__EmitterWallsLast;
      computeTokenFromWalls(window.__EmitterWallsLast);
    }

    const onResize = () => {
      updateVisualAndTokens();
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    return () => {
      window.removeEventListener("EmitterTool:WallsUpdated", onWallsUpdated);
      window.removeEventListener("EmitterTool:UI:GridConfigChange", onGridConfigChange);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      try {
        if (Array.isArray(window.__PA_TOKENS)) {
          window.__PA_TOKENS = window.__PA_TOKENS.filter(
            (t) => t && t.id !== TOKEN_ID
          );
        }
      } catch {
        
      }
    };
  }, []);

  if (screenPos.size <= 0) return null;

  return (
    <div
      id={TOKEN_ID}
      data-token={TOKEN_ID}
      aria-label={TOKEN_ID}
      style={{
        position: "fixed",
        left: screenPos.left,
        top: screenPos.top,
        width: screenPos.size,
        height: screenPos.size,
        borderRadius: "50%",
        background: "#ef4444",
        boxShadow:
          "0 0 0 2px #7f1d1d inset, 0 0 8px rgba(239,68,68,0.6), 0 0 16px rgba(239,68,68,0.35)",
        pointerEvents: "none",
        zIndex: 999999,
      }}
    />
  );
}